import React from "react";
import { Link } from "react-router-dom";
import tammyPhoto from "../assets/tammy.jpg";
import { Mail, Phone } from "lucide-react";
import { motion } from "framer-motion";

const SalesAgent = () => {
  return (
    <section className="py-24 lg:py-32 bg-gray-100 px-6 lg:px-12 overflow-hidden">
      <div className="max-w-7xl mx-auto">
        
        {/* Section Header */}
        <motion.div 
          initial={{ opacity: 0, y: 30 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true, margin: "-100px" }}
          transition={{ duration: 0.8 }}
          className="mb-16"
        >
          <p className="text-sm font-semibold tracking-widest uppercase text-ink-500 mb-3 font-display">
            Meet Our Founder
          </p>
          <h2 className="text-3xl sm:text-4xl lg:text-5xl font-bold tracking-tight text-ink-900 font-display">
            Your St. John Real Estate Expert
          </h2>
        </motion.div>

        {/* Main Content Grid */}
        <div className="grid grid-cols-1 lg:grid-cols-12 gap-12 lg:gap-20 items-start">
          
          {/* Left: Portrait Card */}
          <motion.div 
            initial={{ opacity: 0, x: -40 }}
            whileInView={{ opacity: 1, x: 0 }}
            viewport={{ once: true, margin: "-100px" }}
            transition={{ duration: 0.8 }}
            className="lg:col-span-5 relative group"
          >
            <div className="aspect-[3/4] rounded-3xl overflow-hidden bg-ink-100 relative shadow-2xl">
              <img 
                src={tammyPhoto} 
                alt="Tammy Donnelly" 
                className="w-full h-full object-cover transition-transform duration-700 group-hover:scale-105"
              />
              {/* Overlay Label like the video cards in the design */}
              <div className="absolute bottom-4 left-4 right-4 bg-white/90 backdrop-blur-sm rounded-xl p-4 shadow-lg border border-white/20">
                 <h3 className="text-lg font-bold text-ink-900 font-display">Tammy Donnelly</h3>
                 <p className="text-sm text-ink-600 font-medium">Broker / Owner / ABR®</p>
              </div>
            </div>
          </motion.div>

          {/* Right: Content Section */}
          <motion.div 
            initial={{ opacity: 0, x: 40 }}
            whileInView={{ opacity: 1, x: 0 }}
            viewport={{ once: true, margin: "-100px" }}
            transition={{ duration: 0.8, delay: 0.2 }}
            className="lg:col-span-7 flex flex-col justify-center h-full"
          >
            
            {/* About Section */}
            <div className="mb-12">
              <h4 className="text-xl font-semibold mb-4 text-ink-900 font-display">
                About Tammy
              </h4>
              <p className="text-ink-600 text-lg leading-relaxed font-light">
                Tammy Donnelly is island wise since 1978, a licensed Real Estate agent since 2000, and has owned 340RECO since 2013. She specializes in residential sales and vacation rental management.
              </p>
            </div>

            {/* Areas of Expertise */}
            <div className="mb-12">
              <h4 className="text-xl font-semibold mb-6 text-ink-900 font-display">
                Areas of Expertise
              </h4>
              <div className="flex flex-wrap gap-3">
                <span className="px-5 py-2.5 bg-white border border-gray-200 text-ink-800 rounded-full text-sm font-medium hover:border-tropical-300 transition-colors cursor-default shadow-sm">
                  Residential Sales
                </span>
                <span className="px-5 py-2.5 bg-white border border-gray-200 text-ink-800 rounded-full text-sm font-medium hover:border-tropical-300 transition-colors cursor-default shadow-sm">
                  Vacation Rental Management
                </span>
                <span className="px-5 py-2.5 bg-white border border-gray-200 text-ink-800 rounded-full text-sm font-medium hover:border-tropical-300 transition-colors cursor-default shadow-sm">
                  Property Investment
                </span>
              </div>
            </div>

            {/* Contact Direct */}
            <div className="bg-white rounded-2xl p-8 border border-gray-100 shadow-xl hover:shadow-2xl transition-all duration-500">
              <p className="text-xs font-semibold tracking-widest uppercase text-ink-500 mb-6 font-display">
                Contact Directly
              </p>
              
              <div className="flex flex-col sm:flex-row flex-wrap gap-6 sm:gap-8 mb-6">
                <a 
                  href="mailto:340realestateco@gmail.com"
                  className="flex items-center gap-3 text-ink-900 hover:text-tropical-600 transition-colors group"
                >
                  <div className="w-10 h-10 rounded-full bg-white flex items-center justify-center shadow-sm border border-gray-100 group-hover:border-tropical-200">
                    <Mail className="w-4 h-4" />
                  </div>
                  <span className="font-medium text-sm">340realestateco@gmail.com</span>
                </a>

                <a 
                  href="tel:+13406436068"
                  className="flex items-center gap-3 text-ink-900 hover:text-tropical-600 transition-colors group"
                >
                  <div className="w-10 h-10 rounded-full bg-white flex items-center justify-center shadow-sm border border-gray-100 group-hover:border-tropical-200">
                    <Phone className="w-4 h-4" />
                  </div>
                  <span className="font-medium text-sm">+1 340-643-6068</span>
                </a>

                <a 
                  href="tel:+13407794478"
                  className="flex items-center gap-3 text-ink-900 hover:text-tropical-600 transition-colors group"
                >
                  <div className="w-10 h-10 rounded-full bg-white flex items-center justify-center shadow-sm border border-gray-100 group-hover:border-tropical-200">
                    <Phone className="w-4 h-4" />
                  </div>
                  <span className="font-medium text-sm">+1 340-779-4478</span>
                </a>
              </div>

              <div className="border-t border-gray-100 pt-6">
                <Link 
                  to="/contact" 
                  className="inline-flex items-center justify-center gap-2 px-6 py-3.5 bg-brand-dark hover:bg-brand-dark/95 active:scale-[0.99] text-white text-xs font-bold uppercase tracking-wider rounded-xl transition-all shadow-md w-full sm:w-auto"
                >
                  Send a Message Online
                </Link>
              </div>
            </div>

          </motion.div>
        </div>
      </div>
    </section>
  );
};

export default SalesAgent;
